import Sortable from "../vendor/sortable.esm"
import { showBackofficeFlash } from "packs/init-backoffice-flashes"
import { confirmBackofficeDelete } from "packs/init-backoffice-confirm"
import { initBackofficeUnsavedWarn, markBackofficeFormClean } from "packs/init-backoffice-unsaved-warn"

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

const countLabel = (count) =>
  count > 1 ? `(${count} options)` : `(${count} option)`

const syncOptionsUi = (root) => {
  if (!root) return

  const list = root.querySelector("[data-registration-options]")
  const empty = root.querySelector("[data-registration-options-empty]")
  const hint = root.querySelector("[data-registration-options-hint]")
  const count = list?.querySelectorAll("[data-registration-option]").length || 0

  if (empty) empty.hidden = count > 0
  if (list) list.hidden = count === 0
  if (hint) hint.hidden = count <= 1

  const countEl = root.querySelector("[data-registration-options-count]")
  if (countEl) countEl.textContent = countLabel(count)
}

const optionListItem = (record) => {
  const li = document.createElement("li")
  li.dataset.registrationOption = "true"
  li.dataset.registrationOptionId = String(record.id)

  const handle = document.createElement("span")
  handle.className = "backoffice-faq-handle"
  handle.dataset.registrationOptionHandle = "true"
  handle.title = "Réordonner"
  handle.setAttribute("aria-label", "Réordonner l'option")
  li.appendChild(handle)

  const form = document.createElement("form")
  form.className = "backoffice-photo-card__rename"
  form.action = record.update_url
  form.method = "post"
  form.dataset.registrationOptionRename = "true"

  const method = document.createElement("input")
  method.type = "hidden"
  method.name = "_method"
  method.value = "patch"
  form.appendChild(method)

  const token = document.createElement("input")
  token.type = "hidden"
  token.name = "authenticity_token"
  token.value = csrfToken() || ""
  form.appendChild(token)

  const nameInput = document.createElement("input")
  nameInput.type = "text"
  nameInput.name = "registration_option[name]"
  nameInput.value = record.name || ""
  nameInput.className = "backoffice-input"
  nameInput.required = true
  nameInput.setAttribute("aria-label", "Nom de l'option")
  form.appendChild(nameInput)

  const ok = document.createElement("input")
  ok.type = "submit"
  ok.value = "OK"
  ok.className = "backoffice-btn backoffice-btn--ghost"
  form.appendChild(ok)

  li.appendChild(form)
  bindRenameForm(form)

  const actions = document.createElement("div")
  actions.className = "backoffice-list__actions"
  const destroy = document.createElement("a")
  destroy.href = record.destroy_url
  destroy.className = "backoffice-btn backoffice-btn--danger"
  destroy.dataset.registrationOptionDelete = "true"
  destroy.dataset.confirm = "Supprimer cette option ?"
  destroy.textContent = "Supprimer"
  actions.appendChild(destroy)
  li.appendChild(actions)

  return li
}

const upsertOption = (root, record) => {
  if (!root || !record) return
  const list = root.querySelector("[data-registration-options]")
  if (!list) return

  const existing = list.querySelector(`[data-registration-option-id="${record.id}"]`)
  const item = optionListItem(record)
  if (existing) existing.replaceWith(item)
  else list.appendChild(item)
  syncOptionsUi(root)
}

const removeOption = (root, id) => {
  if (!root || !id) return
  root.querySelector(`[data-registration-option-id="${id}"]`)?.remove()
  syncOptionsUi(root)
}

const patchReorder = async (list, ids) => {
  const url = list.dataset.reorderUrl
  const listKey = list.dataset.listKey
  if (!url || !listKey || ids.length === 0) return true

  const body = new FormData()
  ids.forEach((id) => body.append("option_ids[]", id))
  body.append("list_key", listKey)
  body.append("_method", "patch")

  const response = await fetch(url, {
    method: "POST",
    body,
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrfToken() || "",
    },
    credentials: "same-origin",
  })

  return response.ok
}

const bindSortable = (list) => {
  if (!list || list.dataset.sortableBound === "true") return
  list.dataset.sortableBound = "true"

  Sortable.create(list, {
    animation: 150,
    handle: "[data-registration-option-handle]",
    draggable: "[data-registration-option]",
    ghostClass: "is-dragging",
    onEnd: async () => {
      const ids = Array.from(list.querySelectorAll("[data-registration-option]"))
        .map((item) => item.dataset.registrationOptionId)
        .filter(Boolean)
      const ok = await patchReorder(list, ids)
      if (!ok) showBackofficeFlash("Impossible de réordonner les options", "alert")
    },
  })
}

const bindCreateForm = (form) => {
  if (!form || form.dataset.registrationOptionsBound === "true") return
  form.dataset.registrationOptionsBound = "true"

  form.addEventListener("submit", async (event) => {
    event.preventDefault()
    const root = form.closest("[data-registration-options-root]")
    const submit = form.querySelector('input[type="submit"], button[type="submit"]')
    if (submit) submit.disabled = true

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken() || "",
        },
        credentials: "same-origin",
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        showBackofficeFlash(data.error || "Erreur d'enregistrement", "alert")
        return
      }

      upsertOption(root, data.option)
      form.reset()
      const listKeyInput = form.querySelector('input[name="registration_option[list_key]"]')
      if (listKeyInput && root?.dataset.listKey) listKeyInput.value = root.dataset.listKey
      initBackofficeUnsavedWarn()
      showBackofficeFlash(data.notice || "Option ajoutée.", "notice")
    } catch (_) {
      showBackofficeFlash("Erreur d'enregistrement", "alert")
    } finally {
      if (submit) {
        submit.disabled = false
        submit.removeAttribute("disabled")
      }
    }
  })
}

const bindRenameForm = (form) => {
  if (!form || form.dataset.registrationOptionRenameBound === "true") return
  form.dataset.registrationOptionRenameBound = "true"

  form.addEventListener("submit", async (event) => {
    event.preventDefault()
    const submit = form.querySelector('input[type="submit"], button[type="submit"]')
    if (submit) submit.disabled = true

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken() || "",
        },
        credentials: "same-origin",
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        showBackofficeFlash(data.error || "Erreur d'enregistrement", "alert")
        return
      }

      const input = form.querySelector('input[name="registration_option[name]"]')
      if (input && data.option?.name != null) input.value = data.option.name
      markBackofficeFormClean(form)
      showBackofficeFlash(data.notice || "Nom mis à jour.", "notice")
    } catch (_) {
      showBackofficeFlash("Erreur d'enregistrement", "alert")
    } finally {
      if (submit) {
        submit.disabled = false
        submit.removeAttribute("disabled")
      }
    }
  })
}

const bindDeletes = () => {
  document.querySelectorAll(".backoffice-body").forEach((body) => {
    if (body.dataset.registrationOptionsDeleteBound === "true") return
    body.dataset.registrationOptionsDeleteBound = "true"

    body.addEventListener("click", async (event) => {
      const link = event.target.closest("[data-registration-option-delete]")
      if (!link) return

      event.preventDefault()
      event.stopPropagation()

      const confirmed = await confirmBackofficeDelete({
        title: "Supprimer cette option ?",
        text: link.dataset.confirm || "L'option sera définitivement supprimée.",
      })
      if (!confirmed) return

      const root = link.closest("[data-registration-options-root]")
      const item = link.closest("[data-registration-option-id]")
      const id = item?.dataset.registrationOptionId

      try {
        const response = await fetch(link.href, {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-Token": csrfToken() || "",
          },
          credentials: "same-origin",
        })

        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          showBackofficeFlash(data.error || "Erreur de suppression", "alert")
          return
        }

        removeOption(root, id)
        showBackofficeFlash(data.notice || "Option supprimée.", "notice")
      } catch (_) {
        showBackofficeFlash("Erreur de suppression", "alert")
      }
    })
  })
}

export const initBackofficeRegistrationOptions = () => {
  document.querySelectorAll("[data-registration-options]").forEach(bindSortable)
  document.querySelectorAll("form[data-registration-options-form]").forEach(bindCreateForm)
  document.querySelectorAll("form[data-registration-option-rename]").forEach(bindRenameForm)
  bindDeletes()
}
